import React from 'react'
import { ExternalLink } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'

// HubSpot sprocket mark — brand orange is intentional (a third-party logo), the
// same treatment as the integration cards' HubSpotLogo.
const HubSpotMark: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="none" aria-hidden="true">
    <path
      d="M395.3 199.8v-52.6a44.4 44.4 0 0 0 25.6-40.1V105c0-24.5-19.9-44.4-44.4-44.4h-2.1C349.9 60.6 330 80.5 330 105v2.1c0 17.2 9.8 32.1 24.2 39.5v53.5c-22.8 3.5-43.5 13.1-60.3 27.3L133.2 113.5a48.9 48.9 0 0 0 1.7-12.8C134.9 54.9 97 17 50.2 17S-34.5 54.9-34.5 101.7s37.9 84.7 84.7 84.7c17.6 0 34-5.4 47.5-14.6l159 113.5a132.4 132.4 0 0 0-5.9 39.2c0 27.8 8.6 53.6 23.3 74.9L230.5 443c-3.1-.7-6.3-1.1-9.6-1.1-24.7 0-44.7 20-44.7 44.7s20 44.7 44.7 44.7 44.7-20 44.7-44.7c0-7.4-1.8-14.4-5-20.5l42.1-43.1a132.9 132.9 0 0 0 74.6 22.9c73.5 0 133.1-59.6 133.1-133.1.1-62.9-43.7-115.7-115.1-112.9z"
      fill="#FF7A59"
    />
  </svg>
)

type Props = {
  /** Full HubSpot record URL (see lib/hubspotUrl.ts). Nothing renders when null. */
  href: string | null | undefined
  label?: string
  className?: string
}

/**
 * Consistent "open this record in HubSpot" affordance. First used on the chat
 * screen for record-contextual conversations; reused anywhere a HubSpot object is
 * shown.
 */
export const GoToHubSpotButton: React.FC<Props> = ({ href, label = 'Open in HubSpot', className }) => {
  const { currentTheme: t } = useTheme()
  if (!href) return null

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${className ?? ''}`}
      style={{ color: t.text, border: `1px solid ${t.border}`, backgroundColor: t.secondary }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.hoverBackground }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.secondary }}
    >
      <HubSpotMark />
      <span className="hidden sm:inline">{label}</span>
      <ExternalLink size={12} style={{ color: t.mutedText }} />
    </a>
  )
}

export default GoToHubSpotButton
